/**
 * Skeleton — themed wrapper around `react-loading-skeleton` so we get
 * dark-mode-aware shimmer + a small set of canned shapes:
 *   <Skeleton width={200} />
 *   <Skeleton.Card />
 *   <Skeleton.Text lines={3} />
 *   <Skeleton.Circle size={48} />
 */
import React from 'react';
import RLS, { SkeletonProps as RLSProps, SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useThemeStore } from '../stores/themeStore';
import { css } from './tokens';

interface BaseProps extends RLSProps {}

const SkeletonImpl: React.FC<BaseProps> = (props) => {
  const theme = useThemeStore((s) => s.theme);
  const base = theme === 'dark' ? '#1B2435' : '#E5E7EB';
  const hl   = theme === 'dark' ? '#293245' : '#F3F4F6';
  return (
    <SkeletonTheme baseColor={base} highlightColor={hl} borderRadius={8} duration={1.4}>
      <RLS {...props} />
    </SkeletonTheme>
  );
};

interface SkeletonComponent extends React.FC<BaseProps> {
  Text: React.FC<{ lines?: number; width?: string | number }>;
  Circle: React.FC<{ size?: number }>;
  Card: React.FC<{ height?: number }>;
  ProductCard: React.FC;
  Rail: React.FC<{ items?: number }>;
  Bar: React.FC<{ height?: number; width?: string | number }>;
}

const Skeleton = SkeletonImpl as SkeletonComponent;

Skeleton.Text = ({ lines = 1, width = '100%' }) => (
  <Skeleton count={lines} width={width as any} height={lines === 1 ? 14 : undefined} />
);

Skeleton.Circle = ({ size = 40 }) => (
  <Skeleton circle width={size} height={size} />
);

Skeleton.Card = ({ height = 180 }) => (
  <div
    style={{
      borderRadius: 12,
      background: css.color.surface,
      padding: 14,
      border: `1px solid ${css.color.border}`,
    }}
  >
    <Skeleton height={height * 0.55} />
    <div style={{ marginTop: 10 }}>
      <Skeleton width="80%" height={12} />
    </div>
    <div style={{ marginTop: 6 }}>
      <Skeleton width="55%" height={12} />
    </div>
    <div style={{ marginTop: 10 }}>
      <Skeleton width="40%" height={28} />
    </div>
  </div>
);

Skeleton.ProductCard = () => (
  <div
    style={{
      borderRadius: 12,
      background: css.color.surface,
      padding: 12,
      border: `1px solid ${css.color.border}`,
    }}
  >
    <Skeleton height={110} />
    <div style={{ marginTop: 10 }}><Skeleton width="60%" height={11} /></div>
    <div style={{ marginTop: 6 }}><Skeleton width="90%" height={13} /></div>
    <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Skeleton width={60} height={20} />
      <Skeleton width={64} height={30} />
    </div>
  </div>
);

Skeleton.Rail = ({ items = 5 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items}, minmax(150px, 1fr))`, gap: 12 }}>
    {Array.from({ length: items }).map((_, i) => (
      <Skeleton.ProductCard key={i} />
    ))}
  </div>
);

Skeleton.Bar = ({ height = 8, width = '100%' }) => (
  <Skeleton height={height} width={width as any} />
);

export default Skeleton;
export { Skeleton };

